// src/services/whaleAnalytics.ts
/**
 * 30-Day Whale Activity Analytics
 * Focused on recent selling behavior and percentage-based metrics
 */

import { Redis } from '@upstash/redis';
import { realBlockchainDataService, TokenTransfer } from './realBlockchainData';
import { BlockchainNetwork } from '../types';

// Upstash Redis client for caching balances (browser-safe initialization)
let redis: Redis | null = null;

// Initialize Redis configuration safely
try {
  const redisUrl = process.env.REACT_APP_UPSTASH_REDIS_REST_URL;
  const redisToken = process.env.REACT_APP_UPSTASH_REDIS_REST_TOKEN;
  
  if (redisUrl && redisToken) {
    redis = new Redis({
      url: redisUrl,
      token: redisToken,
    });
    console.log('✅ Whale Analytics Redis client initialized');
  } else {
    console.warn('⚠️  Redis credentials not found - whale analytics caching disabled');
  }
} catch (error) {
  console.warn('⚠️  Failed to initialize Redis client for whale analytics:', error);
}

// ========== Types ==========

interface HistoricalBalance {
  address: string;
  network: BlockchainNetwork;
  balance: number;
  balanceUSD: number;
  timestamp: number;
  source: 'cached' | 'current';
}

interface WhaleActivity30Day {
  address: string;
  network: BlockchainNetwork;
  currentBalance: number | null; // null indicates API failure
  currentBalanceUSD: number | null; // null indicates API failure
  balance30DaysAgo: number;
  balance30DaysAgoUSD: number;
  
  // 30-day activity metrics
  totalTransferredOut: number;
  totalTransferredOutUSD: number;
  percentageOfHoldingsSold: number; // Most important metric!
  
  // Transaction breakdown
  actualSales: number; // To DEX contracts
  walletTransfers: number; // To other wallets
  unknownTransfers: number; // Unclear destination
  
  // Activity classification
  sellingIntensity: 'none' | 'light' | 'moderate' | 'heavy' | 'massive';
  isActiveWhale: boolean;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  
  // Recent activity
  recentTransactions: TokenTransfer[];
  lastTransferDate: number;
  
  // Validation
  validationUrl: string;
  confidence: 'high' | 'medium' | 'low';
  errors: string[];
}

// ========== Main Service ==========

class WhaleAnalyticsService {
  private readonly CACHE_TTL = 30 * 24 * 60 * 60; // 30 days
  private readonly ANALYSIS_WINDOW_DAYS = 30;

  /**
   * Analyze whale activity over the last 30 days
   */
  async analyzeWhaleActivity(address: string, network: BlockchainNetwork): Promise<WhaleActivity30Day> {
    console.log(`🐋 Analyzing 30-day whale activity for ${network} ${address}`);

    try {
      // TEMPORARY: Clear cached historical balance to force fresh calculation with corrected logic
      // This ensures all wallets get recalculated with the fixed balance estimation
      const cacheKey = `whale_balance:${network}:${address}:30d`;
      if (redis) {
        try {
          await redis.del(cacheKey);
          console.log(`🧹 Cleared stale cache for ${address.substring(0, 8)}...`);
        } catch (error) {
          console.log(`Cache clear failed, continuing: ${error}`);
        }
      }
      // Get current blockchain data
      const currentActivity = await realBlockchainDataService.fetchSPX6900Activity(address, network);
      
      // Check if we got valid data from the blockchain service
      if (currentActivity.currentBalance === null || currentActivity.currentBalance === undefined) {
        console.error(`🚨 Blockchain service returned null balance for ${address} - API failure detected`);
        throw new Error('Blockchain API failure - returning error state');
      }
      
      // Get or cache historical balance from 30 days ago
      const historicalBalance = await this.getHistoricalBalance(address, network, 30);
      
      // Analyze 30-day transfers
      const thirtyDaysAgo = Date.now() - (this.ANALYSIS_WINDOW_DAYS * 24 * 60 * 60 * 1000);
      const recentTransactions = currentActivity.recentTransfers.filter(tx => 
        tx.timestamp >= thirtyDaysAgo
      );
      
      // Calculate transfer breakdown
      const transferAnalysis = this.analyzeTransfers(recentTransactions, address);
      
      // Calculate percentage of holdings sold
      const totalTransferredOut = transferAnalysis.totalOut;
      const actualStartingBalance = historicalBalance.balance;

      // SAFEGUARD: If historical balance is suspiciously low compared to current balance + transfers,
      // it means our estimation is wrong. Use a more conservative estimate.
      const minimumReasonableHistoricalBalance = (currentActivity.currentBalance || 0) + totalTransferredOut;
      const finalHistoricalBalance = Math.max(actualStartingBalance, minimumReasonableHistoricalBalance);

      // Calculate percentage with safeguards
      let percentageOfHoldingsSold = 0;
      if (finalHistoricalBalance > 0 && totalTransferredOut > 0) {
        percentageOfHoldingsSold = (totalTransferredOut / finalHistoricalBalance) * 100;

        // FINAL SAFEGUARD: If percentage is over 95%, something is wrong with our calculation
        // (wallets rarely sell 95%+ of holdings). Cap it at a reasonable maximum.
        if (percentageOfHoldingsSold > 95) {
          console.log(`🚨 CAPPING UNREALISTIC SELL % for ${address.substring(0, 8)}...:`);
          console.log(`   Original calculation: ${percentageOfHoldingsSold.toFixed(2)}%`);
          console.log(`   Historical Balance: ${finalHistoricalBalance.toFixed(0)}`);
          console.log(`   Total Transferred: ${totalTransferredOut.toFixed(0)}`);
          console.log(`   Current Balance: ${currentActivity.currentBalance?.toFixed(0) || 'null'}`);

          // If they still hold significant balance, they likely didn't sell much
          if (currentActivity.currentBalance && currentActivity.currentBalance > 100000) {
            percentageOfHoldingsSold = Math.min(percentageOfHoldingsSold, 10); // Cap at 10% for large holders
          } else {
            percentageOfHoldingsSold = Math.min(percentageOfHoldingsSold, 50); // Cap at 50% otherwise
          }
        }
      }
      
      // Calculate USD value for debugging (safe calculation with null checks)
      const pricePerToken = (currentActivity.currentBalanceUSD && currentActivity.currentBalance) 
        ? currentActivity.currentBalanceUSD / Math.max(currentActivity.currentBalance, 1) 
        : 0;
      const totalTransferredOutUSD = totalTransferredOut * pricePerToken;
      
      // Debug logging for whale analytics
      console.log(`🐋 DEBUG ${address.substring(0, 8)}...:`);
      console.log(`   Current Balance: ${currentActivity.currentBalance?.toFixed(0) || 'null'} SPX6900`);
      console.log(`   30d Historical Balance: ${historicalBalance.balance.toFixed(0)} SPX6900`);
      console.log(`   Total Transferred Out (30d): ${totalTransferredOut.toFixed(0)} SPX6900`);
      console.log(`   Percentage Sold: ${percentageOfHoldingsSold.toFixed(2)}%`);
      console.log(`   Recent Transfers (30d): ${recentTransactions.length} transactions`);

      // Enhanced debugging for problematic wallets showing high sell percentages
      if (percentageOfHoldingsSold > 50) {
        console.log(`🚨 HIGH SELL PERCENTAGE DETECTED for ${address.substring(0, 8)}...:`);
        console.log(`   Historical Balance Source: ${historicalBalance.source}`);
        console.log(`   Balance Ratio (historical/current): ${historicalBalance.balance > 0 && currentActivity.currentBalance ? (historicalBalance.balance / currentActivity.currentBalance).toFixed(2) : 'N/A'}x`);

        // Log recent transactions for debugging
        recentTransactions.slice(0, 5).forEach((tx, i) => {
          console.log(`   Tx ${i+1}: ${tx.direction} ${tx.amount.toFixed(0)} SPX6900 on ${new Date(tx.timestamp).toLocaleDateString()}`);
        });

        if (recentTransactions.length > 5) {
          console.log(`   ... and ${recentTransactions.length - 5} more transactions`);
        }
      }
      
      // Determine selling intensity based on percentage
      const sellingIntensity = this.calculateSellingIntensity(percentageOfHoldingsSold);
      
      // Determine risk level
      const riskLevel = this.calculateRiskLevel(percentageOfHoldingsSold, transferAnalysis.actualSales > 0);
      
      const analysis: WhaleActivity30Day = {
        address,
        network,
        currentBalance: currentActivity.currentBalance,
        currentBalanceUSD: currentActivity.currentBalanceUSD,
        balance30DaysAgo: historicalBalance.balance,
        balance30DaysAgoUSD: historicalBalance.balanceUSD,
        
        totalTransferredOut,
        totalTransferredOutUSD,
        percentageOfHoldingsSold,
        
        actualSales: transferAnalysis.actualSales,
        walletTransfers: transferAnalysis.walletTransfers,
        unknownTransfers: transferAnalysis.unknownTransfers,
        
        sellingIntensity,
        isActiveWhale: (currentActivity.currentBalance !== null && currentActivity.currentBalance > 100000) && totalTransferredOut > 0, // 100K+ tokens with activity
        riskLevel,
        
        recentTransactions,
        lastTransferDate: recentTransactions.length > 0 ? Math.max(...recentTransactions.map(tx => tx.timestamp)) : 0,
        
        validationUrl: currentActivity.validationUrl,
        confidence: currentActivity.confidence,
        errors: currentActivity.errors
      };
      
      // Cache current balance for future historical comparisons (only if valid)
      if (currentActivity.currentBalance !== null && currentActivity.currentBalanceUSD !== null) {
        await this.cacheCurrentBalance(address, network, currentActivity.currentBalance, currentActivity.currentBalanceUSD);
      }
      
      console.log(`🐋 ✅ 30-day analysis complete for ${address}:`);
      console.log(`   Current Balance: ${currentActivity.currentBalance !== null ? currentActivity.currentBalance.toFixed(0) + ' SPX6900' : 'Error - API Failed'}`);
      console.log(`   30d Balance: ${historicalBalance.balance.toFixed(0)} SPX6900`);
      console.log(`   Transferred Out: ${totalTransferredOut.toFixed(0)} SPX6900 (${percentageOfHoldingsSold.toFixed(1)}%)`);
      console.log(`   Selling Intensity: ${sellingIntensity}`);
      console.log(`   Risk Level: ${riskLevel}`);
      
      return analysis;
      
    } catch (error) {
      console.error(`❌ Error analyzing whale activity for ${address}:`, error);
      
      // Return error state
      return {
        address,
        network,
        currentBalance: null, // null indicates API failure
        currentBalanceUSD: null, // null indicates API failure
        balance30DaysAgo: 0,
        balance30DaysAgoUSD: 0,
        totalTransferredOut: 0,
        totalTransferredOutUSD: 0,
        percentageOfHoldingsSold: 0,
        actualSales: 0,
        walletTransfers: 0,
        unknownTransfers: 0,
        sellingIntensity: 'none',
        isActiveWhale: false,
        riskLevel: 'low',
        recentTransactions: [],
        lastTransferDate: 0,
        validationUrl: `https://${network.toLowerCase()}scan.${network === 'Solana' ? 'io' : network === 'Base' ? 'org' : 'io'}/address/${address}`,
        confidence: 'low',
        errors: [error?.toString() || 'Unknown error']
      };
    }
  }

  /**
   * Get historical balance from 30 days ago (cached or estimated)
   */
  private async getHistoricalBalance(address: string, network: BlockchainNetwork, daysAgo: number): Promise<HistoricalBalance> {
    const cacheKey = `whale_balance:${network}:${address}:${daysAgo}d`;
    
    try {
      // Try to get cached historical balance (if Redis is available)
      if (redis) {
        const cached = await redis.get(cacheKey);
        if (cached && typeof cached === 'object' && 'balance' in cached) {
          const historicalBalance = cached as HistoricalBalance;
          console.log(`📦 Found cached ${daysAgo}d balance for ${address}: ${historicalBalance.balance}`);
          return historicalBalance;
        }
      }
      
      // If no cached data, estimate based on current balance and recent activity
      console.log(`📦 No cached data for ${address}, estimating historical balance`);
      
      const currentActivity = await realBlockchainDataService.fetchSPX6900Activity(address, network);
      const targetDate = Date.now() - (daysAgo * 24 * 60 * 60 * 1000);
      
      // Calculate net change from transfers in the last 30 days
      const currentBalance = currentActivity.currentBalance || 0;
      let netTransferChange = 0;
      let recentTransferCount = 0;

      currentActivity.recentTransfers.forEach(tx => {
        if (tx.timestamp >= targetDate) {
          recentTransferCount++;
          if (tx.direction === 'outgoing') {
            netTransferChange -= tx.amount; // They had more before sending out
          } else if (tx.direction === 'incoming') {
            netTransferChange += tx.amount; // They had less before receiving
          }
        }
      });

      // If no recent transfers, assume balance hasn't changed
      if (recentTransferCount === 0) {
        console.log(`📦 No transfers in last ${daysAgo} days for ${address.substring(0, 8)}..., using current balance`);
        const estimatedBalance = currentBalance;
        const estimatedBalanceUSD = estimatedBalance * ((currentActivity.currentBalanceUSD && currentActivity.currentBalance)
          ? (currentActivity.currentBalanceUSD / currentActivity.currentBalance)
          : 0);

        const historicalBalance: HistoricalBalance = {
          address,
          network,
          balance: estimatedBalance,
          balanceUSD: estimatedBalanceUSD,
          timestamp: targetDate,
          source: 'current' // No transfers, assumed stable
        };

        return historicalBalance;
      }

      // Estimate 30-day-ago balance by reversing the net change
      let estimatedBalance = Math.max(0, currentBalance - netTransferChange);

      // If the calculated historical balance seems unreasonable compared to current balance,
      // assume minimal change (this handles cases with data inconsistencies)
      const balanceChangeRatio = estimatedBalance > 0 ? (estimatedBalance / (currentBalance || 1)) : 1;
      if (balanceChangeRatio > 100 || balanceChangeRatio < 0.01) {
        console.log(`📦 Unrealistic balance change detected for ${address.substring(0, 8)}... (${balanceChangeRatio.toFixed(2)}x), using current balance as estimate`);
        console.log(`   Current: ${currentBalance.toFixed(0)}, Net Change: ${netTransferChange.toFixed(0)}, Estimated: ${estimatedBalance.toFixed(0)}`);
        estimatedBalance = currentBalance;
      }
      const estimatedBalanceUSD = estimatedBalance * ((currentActivity.currentBalanceUSD && currentActivity.currentBalance) 
        ? (currentActivity.currentBalanceUSD / currentActivity.currentBalance) 
        : 0);
      
      const historicalBalance: HistoricalBalance = {
        address,
        network,
        balance: estimatedBalance,
        balanceUSD: estimatedBalanceUSD,
        timestamp: targetDate,
        source: 'current' // Estimated from current data
      };
      
      console.log(`📦 Estimated ${daysAgo}d balance for ${address}: ${estimatedBalance.toFixed(0)} SPX6900`);
      return historicalBalance;
      
    } catch (error) {
      console.warn(`⚠️ Error getting historical balance for ${address}:`, error);
      
      // Return zero balance as fallback
      return {
        address,
        network,
        balance: 0,
        balanceUSD: 0,
        timestamp: Date.now() - (daysAgo * 24 * 60 * 60 * 1000),
        source: 'current'
      };
    }
  }

  /**
   * Cache current balance for future historical analysis
   */
  private async cacheCurrentBalance(address: string, network: BlockchainNetwork, balance: number, balanceUSD: number): Promise<void> {
    try {
      if (!redis) {
        console.log(`📦 Skipping cache for ${address}: Redis not available`);
        return;
      }
      
      const cacheKey = `whale_balance:${network}:${address}:current`;
      const balanceData: HistoricalBalance = {
        address,
        network,
        balance,
        balanceUSD,
        timestamp: Date.now(),
        source: 'cached'
      };
      
      await redis.setex(cacheKey, this.CACHE_TTL, JSON.stringify(balanceData));
      console.log(`📦 Cached current balance for ${address}: ${balance.toFixed(0)} SPX6900`);
      
    } catch (error) {
      console.warn(`⚠️ Error caching balance for ${address}:`, error);
    }
  }

  /**
   * Analyze transfers to categorize them
   */
  private analyzeTransfers(transactions: TokenTransfer[], address: string) {
    let totalOut = 0;
    let actualSales = 0;
    let walletTransfers = 0;
    let unknownTransfers = 0;
    
    transactions.forEach(tx => {
      if (tx.direction === 'outgoing') {
        totalOut += tx.amount;
        
        // Categorize based on destination
        if (this.isDEXAddress(tx.to)) {
          actualSales += tx.amount;
        } else if (this.isWalletAddress(tx.to)) {
          walletTransfers += tx.amount;
        } else {
          unknownTransfers += tx.amount;
        }
      }
    });
    
    return {
      totalOut,
      actualSales,
      walletTransfers,
      unknownTransfers
    };
  }

  /**
   * Check if address is a known DEX contract
   */
  private isDEXAddress(address: string): boolean {
    const dexAddresses = [
      // Uniswap
      '0x7a250d5630b4cf539739df2c5dacb4c659f2488d', // Uniswap V2 Router
      '0xe592427a0aece92de3edee1f18e0157c05861564', // Uniswap V3 Router
      // Add more DEX addresses as needed
    ];
    
    return dexAddresses.includes(address.toLowerCase());
  }

  /**
   * Check if address looks like a regular wallet
   */
  private isWalletAddress(address: string): boolean {
    // Simple heuristic - not a known contract
    return !this.isDEXAddress(address) && address.length > 0;
  }

  /**
   * Calculate selling intensity based on percentage sold
   */
  private calculateSellingIntensity(percentageSold: number): 'none' | 'light' | 'moderate' | 'heavy' | 'massive' {
    if (percentageSold === 0) return 'none';
    if (percentageSold < 5) return 'light';      // < 5%
    if (percentageSold < 15) return 'moderate';  // 5-15%
    if (percentageSold < 30) return 'heavy';     // 15-30%
    return 'massive';                            // > 30%
  }

  /**
   * Calculate risk level for market impact
   */
  private calculateRiskLevel(percentageSold: number, hasActualSales: boolean): 'low' | 'medium' | 'high' | 'critical' {
    if (percentageSold === 0) return 'low';
    
    if (hasActualSales) {
      // Actual DEX sales are more concerning
      if (percentageSold > 20) return 'critical';
      if (percentageSold > 10) return 'high';
      if (percentageSold > 3) return 'medium';
    } else {
      // Just transfers between wallets
      if (percentageSold > 40) return 'high';
      if (percentageSold > 20) return 'medium';
      if (percentageSold > 10) return 'medium';
    }
    
    return 'low';
  }

  /**
   * Analyze multiple whale addresses
   */
  async analyzeMultipleWhales(addresses: Array<{address: string, network: BlockchainNetwork}>): Promise<WhaleActivity30Day[]> {
    console.log(`🐋 Analyzing ${addresses.length} whale addresses`);
    
    const analyses = await Promise.all(
      addresses.map(({address, network}) => 
        this.analyzeWhaleActivity(address, network)
      )
    );
    
    // Sort by risk level and percentage sold
    return analyses.sort((a, b) => {
      const riskOrder = {'critical': 4, 'high': 3, 'medium': 2, 'low': 1};
      const aRisk = riskOrder[a.riskLevel];
      const bRisk = riskOrder[b.riskLevel];
      
      if (aRisk !== bRisk) return bRisk - aRisk; // High risk first
      return b.percentageOfHoldingsSold - a.percentageOfHoldingsSold; // Higher percentage first
    });
  }
}

// Export the service
export const whaleAnalyticsService = new WhaleAnalyticsService();
export default whaleAnalyticsService;
export type { WhaleActivity30Day, HistoricalBalance };